import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatPrice } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Upsell strategy mapping
const UPSELL_CATEGORIES = {
  'Modular Office Workstation': ['Storage Cabinets', 'Privacy Booths'],
  'Storage Cabinets': ['Modular Office Workstation', 'Privacy Booths'],
  'Privacy Booths': ['Modular Office Workstation', 'Storage Cabinets']
};

const UPSELL_MESSAGES = {
  'Modular Office Workstation': {
    'Storage Cabinets': 'Complete your workspace with matching storage',
    'Privacy Booths': 'Add privacy for focused work'
  },
  'Storage Cabinets': {
    'Modular Office Workstation': 'Pair with our premium workstations',
    'Privacy Booths': 'Create private meeting spaces'
  },
  'Privacy Booths': {
    'Modular Office Workstation': 'Set up collaborative workspaces',
    'Storage Cabinets': 'Organize your office essentials'
  }
};

export default function ProductUpsell({ currentCategory, currentProductId }) {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentCategory) return;
    
    const fetchRecommendations = async () => {
      try {
        const targetCategories = UPSELL_CATEGORIES[currentCategory] || [];
        if (targetCategories.length === 0) {
          setLoading(false);
          return;
        }

        const response = await axios.get(`${API_URL}/products`);
        const allProducts = response.data.products || response.data || [];
        
        // Get products from recommended categories (2 from each)
        const recommended = [];
        for (const category of targetCategories) {
          const categoryProducts = allProducts
            .filter(p => p.category === category && p.id !== currentProductId)
            .slice(0, 2);
          
          categoryProducts.forEach(p => {
            recommended.push({
              ...p,
              upsellMessage: UPSELL_MESSAGES[currentCategory]?.[category] || 'You might also like'
            });
          });
        }
        
        setRecommendations(recommended.slice(0, 4));
      } catch (error) {
        console.error('Error fetching recommendations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecommendations();
  }, [currentCategory, currentProductId]);

  if (loading || recommendations.length === 0) return null;

  return (
    <div className="mt-16 border-t-2 border-black pt-12">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-2xl md:text-3xl font-black uppercase">
          Complete Your Office Setup
        </h2>
        <Link to="/products">
          <Button variant="outline" className="rounded-none">
            View All <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {recommendations.map((product) => (
          <Link 
            key={product.id} 
            to={`/products/${product.id}`}
            className="group border-2 border-gray-200 hover:border-black transition-colors"
          >
            <div className="relative overflow-hidden bg-gray-100">
              <img
                src={product.image_url || product.image_urls?.[0]}
                alt={product.name}
                className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute top-2 left-2 bg-yellow-400 text-black text-xs font-bold px-2 py-1">
                RECOMMENDED
              </div>
            </div>
            
            <div className="p-4">
              <p className="text-xs text-gray-500 uppercase mb-1">{product.category}</p>
              <h3 className="font-bold text-sm mb-1 line-clamp-1">{product.name}</h3>
              <p className="text-xs text-gray-600 mb-2 italic">{product.upsellMessage}</p>
              
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-black text-lg">{formatPrice(product.price)}</span>
                  {product.original_price && product.original_price > product.price && (
                    <span className="text-xs text-gray-400 line-through ml-2">
                      {formatPrice(product.original_price)}
                    </span>
                  )}
                </div>
                <Button size="sm" className="rounded-none bg-black hover:bg-gray-800">
                  <ShoppingCart className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Upsell Banner */}
      <div className="mt-8 bg-gradient-to-r from-yellow-400 to-yellow-500 p-6 flex items-center justify-between">
        <div>
          <h3 className="text-xl font-black uppercase">Bundle & Save!</h3>
          <p className="text-sm">Buy together and get exclusive discounts. Contact us for bulk pricing.</p>
        </div>
        <Link to="/bulk-inquiry">
          <Button className="bg-black text-white hover:bg-gray-800 rounded-none uppercase font-bold">
            Get Quote <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  );
}
